import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import { t } from "@/localization";
import { colors } from "./theme";

// Save status for a settings screen. Each auto-saving form reports what it is
// doing; the screen header shows one summary instead of a toast per save:
//   Saving…  ·  Saved  ·  Not saved: <reason>  ·  Couldn't save · Retry  ·  Unsaved changes
// EventSettingsScreenContainer provides it. Outside a provider, reporters get
// null and fall back to toasts (see AutoSave).

const SaveStatusContext = createContext(null);

const PRIORITY = {
  saving: 5,
  error: 4,
  invalid: 3,
  unsaved: 2,
  pending: 1,
  saved: 0,
};

export function summarize(entries) {
  const list = Object.values(entries).filter(Boolean);
  if (!list.length) return null;
  list.sort(
    (a, b) =>
      (PRIORITY[b.state] ?? -1) - (PRIORITY[a.state] ?? -1) ||
      (b.at || 0) - (a.at || 0)
  );
  return list[0];
}

export function SaveStatusProvider({ children }) {
  const [entries, setEntries] = useState({});
  const report = useCallback((id, status) => {
    setEntries((current) => {
      if (!status) {
        if (!(id in current)) return current;
        const next = { ...current };
        delete next[id];
        return next;
      }
      const prev = current[id];
      if (
        prev &&
        prev.state === status.state &&
        prev.message === status.message &&
        prev.at === status.at
      ) {
        return current;
      }
      return { ...current, [id]: status };
    });
  }, []);
  const value = useMemo(
    () => ({ report, status: summarize(entries) }),
    [report, entries]
  );
  return (
    <SaveStatusContext.Provider value={value}>
      {children}
    </SaveStatusContext.Provider>
  );
}

export const useSaveStatus = () => useContext(SaveStatusContext);

// Report this form's status: report({ state, message?, retry? }) or report(null).
export function useSaveStatusReporter(id) {
  const ctx = useContext(SaveStatusContext);
  const report = ctx?.report;
  useEffect(() => () => report?.(id, null), [report, id]);
  return useMemo(
    () => (report ? (status) => report(id, status) : null),
    [report, id]
  );
}

// For forms with their own Save button: the header says "Unsaved changes"
// while `dirty`. <UnsavedChanges dirty={formik.dirty} />
export function UnsavedChanges({ dirty }) {
  const id = React.useId();
  const report = useSaveStatusReporter(id);
  useEffect(() => {
    report?.(dirty ? { state: "unsaved", at: Date.now() } : null);
  }, [dirty, report]);
  return null;
}

const Row = ({ icon, color, label, spinner, onPress, testID }) => {
  const content = (
    <View style={tw`flex-row items-center`} testID={testID}>
      {spinner ? (
        <ActivityIndicator
          size="small"
          color={colors.muted}
          style={tw`mr-1.5`}
        />
      ) : (
        <Ionicons name={icon} size={16} color={color} style={tw`mr-1`} />
      )}
      <Text
        numberOfLines={1}
        style={[tw`text-[13px] leading-[18px]`, { color }]}
      >
        {label}
      </Text>
    </View>
  );
  return onPress ? (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={t("{label}. Try saving again", { label })}
      hitSlop={8}
      style={({ hovered }) => [
        tw`rounded-md px-1.5 py-1`,
        hovered && { backgroundColor: colors.dangerTint },
      ]}
    >
      {content}
    </Pressable>
  ) : (
    <View style={tw`px-1.5 py-1`}>{content}</View>
  );
};

// The summary, for a screen header.
export function SaveStatusIndicator({ style }) {
  const ctx = useSaveStatus();
  const status = ctx?.status;
  // "Saved" fades to nothing after a while; the state stays "saved".
  const [quiet, setQuiet] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    setQuiet(false);
    clearTimeout(timer.current);
    if (status?.state === "saved") {
      timer.current = setTimeout(() => setQuiet(true), 6000);
    }
    return () => clearTimeout(timer.current);
  }, [status?.state, status?.at]);

  if (!status) return null;
  let row = null;
  switch (status.state) {
    case "saving":
    case "pending":
      row = (
        <Row
          spinner
          label={t("Saving…")}
          color={colors.muted}
          testID="save-status-saving"
        />
      );
      break;
    case "saved":
      if (quiet) return null;
      row = (
        <Row
          icon="checkmark-circle"
          label={t("Saved")}
          color={colors.success}
          testID="save-status-saved"
        />
      );
      break;
    case "error":
      row = (
        <Row
          icon="alert-circle"
          label={t("Couldn't save · Retry")}
          color={colors.danger}
          onPress={status.retry}
          testID="save-status-error"
        />
      );
      break;
    case "invalid":
      row = (
        <Row
          icon="alert-circle"
          label={
            status.message
              ? t("Not saved: {message}", { message: status.message })
              : t("Not saved")
          }
          color={colors.warning}
          testID="save-status-invalid"
        />
      );
      break;
    case "unsaved":
      row = (
        <Row
          icon="ellipse"
          label={t("Unsaved changes")}
          color={colors.warning}
          testID="save-status-unsaved"
        />
      );
      break;
    default:
      return null;
  }
  return (
    <View accessibilityLiveRegion="polite" style={[tw`flex-shrink`, style]}>
      {row}
    </View>
  );
}
